"use client"

import { useState } from "react"
import { ArrowRightLeft, Banknote, Handshake, Loader2, Plus, Users, Wallet } from "lucide-react"
import { usePermissions } from "@/hooks/use-permissions"
import { usePartnerLedgerQuery, usePartnerOverviewQuery, usePartnerPayoutsQuery } from "@/hooks/queries/partners"
import { RequestPayoutDialog, TransferToWalletDialog } from "@/components/partners/partner-payout-dialogs"
import { PartnerCodeDialog } from "@/components/partners/partner-code-dialog"
import { ReferralLinkRow } from "@/components/partners/referral-link-row"
import { useActivatePartnerProgramMutation, useSetPartnerCodeActiveMutation } from "@/hooks/mutations/partners"
import { PARTNER_ACCOUNT_STATUSES, PARTNER_BALANCE_OPERATION_TYPES, PARTNER_PAYOUT_STATUSES } from "@/lib/partners/constants"
import type { PartnerBalanceOperationType, PartnerPayoutStatus } from "@/lib/partners/types"
import type { PartnerCodeView } from "@/server-actions/partners"
import { PARTNER_PROGRAM_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { formatMicroUsd, formatMicroUsdSigned } from "@/lib/billing/utils/format"
import { formatPartnerDate } from "@/components/partners/format-date"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"





export function PartnerProgramPage() {


  const tUi = useTranslations("Partners.PartnersPartnerProgramPage")

  const { can } = usePermissions()
  const overview = usePartnerOverviewQuery()
  const ledger = usePartnerLedgerQuery(LEDGER_PAGE_SIZE)
  const ledgerEntries = ledger.data?.pages.flatMap(page => page.entries) ?? []
  const payouts = usePartnerPayoutsQuery()
  const [openDialog, setOpenDialog] = useState<"transfer" | "payout" | null>(null)
  const isSuspended =
    overview.data?.activated === true
    && overview.data.partner_status === PARTNER_ACCOUNT_STATUSES.SUSPENDED
  const activateMutation = useActivatePartnerProgramMutation()
  const setCodeActiveMutation = useSetPartnerCodeActiveMutation()
  const [codeDialog, setCodeDialog] = useState<{ open: boolean; code: PartnerCodeView | null }>({ open: false, code: null })

  if (!can(PARTNER_PROGRAM_PERMISSIONS.MANAGE)) {
    return (
      <div className="p-6">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <AlertTitle><span key={tUi("accessRestricted")}>{tUi("accessRestricted")}</span></AlertTitle>
            <AlertDescription><span key={tUi("youDonTHavePermissionToManageThe")}>{tUi("youDonTHavePermissionToManageThe")}</span></AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mx-auto max-w-4xl space-y-6">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold"><TranslationText>{tUi.rich("tag0Tag0PartnerProgram", { tag0: () => (<Handshake className="h-5 w-5" />) })}</TranslationText></h1>
          <p className="mt-1 text-sm text-muted-foreground">
            <span key={tUi("shareYourLinkAndEarnACommissionOn")}>{tUi("shareYourLinkAndEarnACommissionOn")}</span></p>
        </div>

        {overview.isError && (
          <Alert variant="destructive"><AlertDescription><span key={tUi("unableToLoadThePartnerProgramPleaseRefresh")}>{tUi("unableToLoadThePartnerProgramPleaseRefresh")}</span></AlertDescription></Alert>
        )}

        {overview.isLoading ? (
          <div className="flex min-h-40 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> <span key={tUi("loadingPartnerProgram")}>{tUi("loadingPartnerProgram")}</span></div>
        ) : overview.data?.activated === false ? (
          <Card>
            <CardHeader>
              <CardTitle><span key={tUi("becomeAPartner")}>{tUi("becomeAPartner")}</span></CardTitle>
              <CardDescription>
                {<span key={tUi("youEarnValue0OfEveryPaymentMadeBy", { value0: String(overview.data.commission_percent) })}>{tUi("youEarnValue0OfEveryPaymentMadeBy", { value0: String(overview.data.commission_percent) })}</span>}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => activateMutation.mutate()} disabled={activateMutation.isPending}>
                {activateMutation.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                <span key={activateMutation.isPending ? tUi("activating") : tUi("becomeAPartner")}>{activateMutation.isPending ? tUi("activating") : tUi("becomeAPartner")}</span>
              </Button>
            </CardContent>
          </Card>
        ) : overview.data ? (
          <>
            {isSuspended && (
              <Alert variant="destructive">
                <AlertTitle><span key={tUi("partnershipSuspended")}>{tUi("partnershipSuspended")}</span></AlertTitle>
                <AlertDescription><span key={tUi("yourLinksNoLongerAttributeNewClientsNo")}>{tUi("yourLinksNoLongerAttributeNewClientsNo")}</span></AlertDescription>
              </Alert>
            )}

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="pb-2"><CardDescription className="flex items-center gap-1.5"><TranslationText>{tUi.rich("tag0Tag0PartnerBalance", { tag0: () => (<Wallet className="h-3.5 w-3.5" />) })}</TranslationText></CardDescription></CardHeader>
                <CardContent><p className="text-2xl font-semibold">{formatMicroUsd(overview.data.balance_microusd)}</p></CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardDescription><span key={tUi("earnedInTotal")}>{tUi("earnedInTotal")}</span></CardDescription></CardHeader>
                <CardContent><p className="text-2xl font-semibold">{formatMicroUsd(overview.data.lifetime_earned_microusd)}</p></CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardDescription><span key={tUi("yourCommission")}>{tUi("yourCommission")}</span></CardDescription></CardHeader>
                <CardContent><p className="text-2xl font-semibold">{overview.data.commission_percent}%</p></CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardDescription className="flex items-center gap-1.5"><TranslationText>{tUi.rich("tag0Tag0ReferredClients", { tag0: () => (<Users className="h-3.5 w-3.5" />) })}</TranslationText></CardDescription></CardHeader>
                <CardContent><p className="text-2xl font-semibold">{overview.data.referred_count}</p></CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader className="flex flex-col items-start justify-between gap-4 space-y-0 lg:flex-row">
                <div className="min-w-0 space-y-1.5">
                  <CardTitle className="text-base"><span key={tUi("referralLinks")}>{tUi("referralLinks")}</span></CardTitle>
                  <CardDescription>
                    <span key={tUi("shareALinkOrGiveOutItsCode")}>{tUi("shareALinkOrGiveOutItsCode")}</span></CardDescription>
                </div>
                <Button size="sm" className="shrink-0" onClick={() => setCodeDialog({ open: true, code: null })}>
                  <Plus className="mr-1.5 h-4 w-4" /> <span key={tUi("newLink")}>{tUi("newLink")}</span></Button>
              </CardHeader>
              <CardContent>
                <div className="divide-y rounded-lg border">
                  {overview.data.codes.map(code => (
                    <ReferralLinkRow
                      key={code.id}
                      code={code}
                      onEdit={() => setCodeDialog({ open: true, code })}
                      onToggleActive={() => setCodeActiveMutation.mutate({ id: code.id, is_active: !code.is_active })}
                      togglePending={setCodeActiveMutation.isPending}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>

            {overview.data.withdrawal.wallet_transfer_enabled || overview.data.withdrawal.cash_payout_enabled ? (
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                {overview.data.withdrawal.wallet_transfer_enabled && (
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto"
                    disabled={overview.data.balance_microusd <= 0 || isSuspended}
                    onClick={() => setOpenDialog("transfer")}
                  >
                    <ArrowRightLeft className="mr-1.5 h-4 w-4" /> <span key={tUi("transferToWallet")}>{tUi("transferToWallet")}</span></Button>
                )}
                {overview.data.withdrawal.cash_payout_enabled && (
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto"
                    disabled={overview.data.balance_microusd < overview.data.min_payout_microusd}
                    onClick={() => setOpenDialog("payout")}
                  ><TranslationText>{tUi.rich("tag0Tag0RequestPayout", { tag0: () => (<Banknote className="mr-1.5 h-4 w-4" />) })}</TranslationText></Button>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                <span key={tUi("withdrawalsAreCurrentlyUnavailableYourEarningsStayOn")}>{tUi("withdrawalsAreCurrentlyUnavailableYourEarningsStayOn")}</span></p>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-base"><span key={tUi("payoutRequests")}>{tUi("payoutRequests")}</span></CardTitle>
              </CardHeader>
              <CardContent>
                {payouts.isError ? (
                  <p className="text-sm text-destructive"><span key={tUi("unableToLoadYourPayoutRequestsPleaseRefresh")}>{tUi("unableToLoadYourPayoutRequestsPleaseRefresh")}</span></p>
                ) : payouts.isPending ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> <span key={tUi("loadingRequests")}>{tUi("loadingRequests")}</span></div>
                ) : payouts.data.length ? (
                  <Table className="min-w-0">
                    <TableHeader>
                      <TableRow>
                        <TableHead><span key={tUi("date")}>{tUi("date")}</span></TableHead>
                        <TableHead className="text-right"><span key={tUi("requested")}>{tUi("requested")}</span></TableHead>
                        <TableHead className="text-right"><span key={tUi("fee")}>{tUi("fee")}</span></TableHead>
                        <TableHead className="text-right"><span key={tUi("youReceive")}>{tUi("youReceive")}</span></TableHead>
                        <TableHead><span key={tUi("status")}>{tUi("status")}</span></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payouts.data.map(payout => {
                        // A rejected request returned the whole amount, so nothing was withheld or received.
                        const rejected = payout.status === PARTNER_PAYOUT_STATUSES.REJECTED
                        return (
                          <TableRow key={payout.id}>
                            <TableCell className="whitespace-nowrap text-muted-foreground">{formatPartnerDate(payout.created_at)}</TableCell>
                            <TableCell className="whitespace-nowrap text-right">{formatMicroUsd(payout.amount_microusd)}</TableCell>
                            <TableCell className="whitespace-nowrap text-right">{rejected ? "—" : formatMicroUsd(payout.fee_microusd)}</TableCell>
                            <TableCell className="whitespace-nowrap text-right">
                              {rejected ? "—" : formatMicroUsd(payout.amount_microusd - payout.fee_microusd)}
                            </TableCell>
                            <TableCell>
                              <span>{PAYOUT_STATUS_LABELS[payout.status]}</span>
                              {payout.admin_note && <p className="text-xs text-muted-foreground">{payout.admin_note}</p>}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-muted-foreground"><span key={tUi("noPayoutRequestsYet")}>{tUi("noPayoutRequestsYet")}</span></p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base"><span key={tUi("recentEarnings")}>{tUi("recentEarnings")}</span></CardTitle>
                <CardDescription><span key={tUi("commissionTransfersToYourWalletAndPayouts")}>{tUi("commissionTransfersToYourWalletAndPayouts")}</span></CardDescription>
              </CardHeader>
              <CardContent>
                {ledger.isError ? (
                  <p className="text-sm text-destructive"><span key={tUi("unableToLoadYourEarningsHistoryPleaseRefresh")}>{tUi("unableToLoadYourEarningsHistoryPleaseRefresh")}</span></p>
                ) : ledger.isPending ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> <span key={tUi("loadingEarnings")}>{tUi("loadingEarnings")}</span></div>
                ) : ledgerEntries.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <tbody>
                        {ledgerEntries.map(entry => (
                          <tr key={entry.id} className="border-b last:border-0">
                            <td className="py-2 pr-4 whitespace-nowrap text-muted-foreground">{formatPartnerDate(entry.created_at)}</td>
                            <td className="py-2 pr-4">
                              {LEDGER_LABELS[entry.operation_type]}
                              {/* Only adjustments carry a note worth reading — every other
                                  description just restates the label. */}
                              {entry.operation_type === PARTNER_BALANCE_OPERATION_TYPES.MANUAL_ADJUSTMENT
                                && entry.description && (
                                <p className="text-xs text-muted-foreground">{entry.description}</p>
                              )}
                            </td>
                            <td className="py-2 text-right font-medium whitespace-nowrap">
                              {formatMicroUsdSigned(entry.amount_microusd)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {ledger.hasNextPage && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        disabled={ledger.isFetchingNextPage}
                        onClick={() => ledger.fetchNextPage()}
                      >
                        {ledger.isFetchingNextPage && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} <span key={tUi("showMore")}>{tUi("showMore")}</span></Button>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    <span key={tUi("nothingYetEarningsAppearHereOnceAReferred")}>{tUi("nothingYetEarningsAppearHereOnceAReferred")}</span></p>
                )}
              </CardContent>
            </Card>
          </>
        ) : null}
      </div>

      {overview.data?.activated ? (
        <>
          <TransferToWalletDialog
            open={openDialog === "transfer"}
            balanceMicrousd={overview.data.balance_microusd}
            creditLifetimeMonths={overview.data.credit_lifetime_months}
            onOpenChange={next => setOpenDialog(next ? "transfer" : null)}
          />
          <RequestPayoutDialog
            open={openDialog === "payout"}
            balanceMicrousd={overview.data.balance_microusd}
            minPayoutMicrousd={overview.data.min_payout_microusd}
            feePercent={overview.data.withdrawal.cash_payout_fee_percent}
            onOpenChange={next => setOpenDialog(next ? "payout" : null)}
          />
          <PartnerCodeDialog
            open={codeDialog.open}
            code={codeDialog.code}
            partnerPercent={overview.data.commission_percent}
            onOpenChange={open => setCodeDialog(current => ({ ...current, open }))}
          />
        </>
      ) : null}
    </div>
  )
}

const LEDGER_PAGE_SIZE = 20

const PAYOUT_STATUS_LABELS: Record<PartnerPayoutStatus, string> = {
  pending: "Pending",
  completed: "Paid",
  rejected: "Rejected",
}

const LEDGER_LABELS: Record<PartnerBalanceOperationType, string> = {
  commission: "Referral commission",
  transfer_to_wallet: "Transferred to wallet",
  payout_hold: "Payout requested",
  payout_reversal: "Payout rejected",
  manual_adjustment: "Adjustment",
}
